import { useEffect, useRef, useState, useCallback } from 'react';
import { buildGoogleMapsLink, parseLatLngFromGoogleMapsUrl } from '../Components/GeneralContactForm';

const CONTACT_FIELDS = {
    crmId: { default: '' },
    crmTeamId: { default: '' },
    crmStageId: { default: '' },
    crmStageName: { default: '', type: 'string' }, // display-only; harmless if sent
    crmSalesRep: { default: '' },
    crmContactFirst: { default: '', type: 'string' },
    crmContactMiddle: { default: '', type: 'string' },
    crmContactLast: { default: '', type: 'string' },
    crmTitle: { default: '', type: 'string' },
    crmCompany: { default: '', type: 'string' },
    crmIndustry: { default: '', type: 'string' },
    crmProjectType: { default: '', type: 'string' },
    crmPhoneCountryCode: { default: '+353' },
    crmContactPhone: { default: '', type: 'string' },
    crmContactEmail: { default: '', type: 'string' },
    crmLinkedInProfile: { default: '', type: 'string' },
    crmAddressLine1: { default: '', type: 'string' },
    crmAddressLine2: { default: '', type: 'string' },
    crmAddressTown: { default: '', type: 'string' },
    crmAddressCity: { default: '', type: 'string' },
    crmAddressCountry: { default: 'Ireland', type: 'string' },
    crmAddressZip: { default: '', type: 'string' },
    crmAddressCounty: { default: '', type: 'string' },
    fellingLicenseNo: { default: '', type: 'string' },
    fellingLicenseStatus: { default: '', type: 'string' },
    dropPinLatitude: { default: null, type: 'number' },  // the field that broke Add
    dropPinLongitude: { default: null, type: 'number' },
    dropPin: { default: '', type: 'string' },
    crmBackgroundInfo: { default: '', type: 'string' },
    crmContactWebsite: { default: '', type: 'string' },
    crmDateOfInitialContact: { default: null, type: 'date' },
    crmProposalDueDate: { default: null, type: 'date' },
    leadQuality: { default: '', type: 'string' },
    crmRating: { default: '', type: 'string' },
    crmContactTitle: { default: '', type: 'string' },
    crmLeadReferralSource: { default: '', type: 'string' },
    crmContactStatus: { default: '', type: 'string' },
    crmProjectDescription: { default: '', type: 'string' },

    crmBudget: { editOnly: true, default: null },
    crmDeliverable: { editOnly: true, default: null },
    crmDoNotContact: { editOnly: true, default: false },
    crmDoNotEmail: { editOnly: true, default: false },
};

function coerceValue(type, value) {
    switch (type) {
        case 'string':
            return value != null ? String(value) : '';
        case 'date': {
            // Explicitly treat 0, '0', empty string, or null as null
            if (!value || value === 0 || value === '0') return null;
            const d = new Date(value);
            return !isNaN(d.getTime()) ? d.toISOString() : null;
        }
        case 'number': {
            if (value === '' || value == null) return null;
            const n = Number(value);
            return Number.isNaN(n) ? null : n;
        }
        default:
            return value;
    }
}

const INITIAL_GENERAL = Object.fromEntries(
    Object.entries(CONTACT_FIELDS)
        .filter(([, cfg]) => !cfg.editOnly)
        .map(([key, cfg]) => [key, cfg.default]),
);

function pickKnownGeneralFields(row) {
    const picked = {};
    Object.keys(INITIAL_GENERAL).forEach((key) => {
        if (row[key] === undefined) return;
        picked[key] = coerceValue(CONTACT_FIELDS[key].type, row[key]);
    });
    return picked;
}

const INITIAL_ENQUIRY = {
    id: '',
    enquiryRating: '',
    submissionDate: '',
    transactionType: '',
    enquiryType: '',
    worrellRepDetails: '',
    enquiryOriginatedFrom: '',
    forestersName: '',
    jobType: '',
    forestDescription: '',
    sizeInHectares: '',
    managedBy: '',
    allocatedTo: '',
    pipelineStatus: '',
    forestryLicenseViewer: '',
    landDirectViewer: '',
    additionalDetails: '',
    followupDate: '',
    followupDetails: '',
    sendBrochure: '',
    additionalNoteWithBrochure: '',
};

function buildContactPayload(generalForm, isEdit) {
    const payload = {};
    Object.entries(CONTACT_FIELDS).forEach(([key, cfg]) => {
        if (cfg.editOnly && !isEdit) return;

        const raw = key in generalForm ? generalForm[key] : cfg.default;
        const coerced = coerceValue(cfg.type, raw);

        // Omit null/0 date fields entirely so ASP.NET defaults them to null
        if (cfg.type === 'date' && coerced === null) {
            return;
        }

        payload[key] = coerced;
    });
    return payload;
}

// Ireland/UK phone validation, shared by the General tab.
export const validatePhone = (countryCode, number) => {
    if (!number) return true; // optional field
    const clean = number.replace(/[\s-]/g, '');
    if (countryCode === '+353') return /^[1-9]\d{7,8}$/.test(clean); // Ireland
    if (countryCode === '+44') return /^[1-9]\d{9,10}$/.test(clean); // UK
    return false;
};

export default function useContactForm({
    open,
    contactData,
    teams = [],
    stages = [],
    teamMembers = [],
    selectedTeam = {},
    currentStage = null,
    currentUserEmail = '',
    onFetchEnquiry,
    onSaveContact,
    onSaveEnquiry,
    onEnquiryAutoCreated,
    onEnquiryFetchError,
    onClose
}) {
    const [activeTab, setActiveTab] = useState('general');
    const [generalForm, setGeneralForm] = useState(INITIAL_GENERAL);
    const [generalErrors, setGeneralErrors] = useState({});
    const [enquiryForm, setEnquiryForm] = useState(INITIAL_ENQUIRY);
    const [enquiryErrors, setEnquiryErrors] = useState({});
    const [savingGeneral, setSavingGeneral] = useState(false);
    const [savingEnquiry, setSavingEnquiry] = useState(false);


    const [enquiryExists, setEnquiryExists] = useState(false);
    const [enquiryLoading, setEnquiryLoading] = useState(false);
    const [enquiryFetchError, setEnquiryFetchError] = useState(null);

    const originalGeneralRef = useRef(INITIAL_GENERAL);
    const originalEnquiryRef = useRef(INITIAL_ENQUIRY);


    const onFetchEnquiryRef = useRef(onFetchEnquiry);
    const onEnquiryFetchErrorRef = useRef(onEnquiryFetchError);
    useEffect(() => {
        onFetchEnquiryRef.current = onFetchEnquiry;
    }, [onFetchEnquiry]);
    useEffect(() => {
        onEnquiryFetchErrorRef.current = onEnquiryFetchError;
    }, [onEnquiryFetchError]);

    const isEdit = Boolean(contactData && contactData.crmId);

    useEffect(() => {
        if (!open) return;

        let general;
        if (contactData) {
            let phoneCode = '+353';
            let rawPhone = contactData.crmContactPhone || '';
            if (rawPhone.startsWith('+44')) {
                phoneCode = '+44';
                rawPhone = rawPhone.replace('+44', '').trim();
            } else if (rawPhone.startsWith('+353')) {
                phoneCode = '+353';
                rawPhone = rawPhone.replace('+353', '').trim();
            }
            general = {
                ...INITIAL_GENERAL,
                ...pickKnownGeneralFields(contactData), // was: ...contactData
                crmPhoneCountryCode: phoneCode,
                crmContactPhone: rawPhone,
            };
            if (!general.dropPin && general.dropPinLatitude && general.dropPinLongitude) {
                general.dropPin = buildGoogleMapsLink(general.dropPinLatitude, general.dropPinLongitude);
            }
        } else {
            general = {
                ...INITIAL_GENERAL,
                crmTeamId: selectedTeam?.value || (teams[0] ? teams[0].value : ''),
                crmStageName: currentStage?.label ?? (stages[0] ? stages[0].label : ''),
                crmStageId: currentStage?.StageId ?? (stages[0] ? stages[0].value : ''),
                crmSalesRep: currentUserEmail || '',
            };
        }

        setGeneralForm(general);
        originalGeneralRef.current = general;
        setGeneralErrors({});
        setEnquiryErrors({});
        setActiveTab('general');

        setEnquiryForm(INITIAL_ENQUIRY);
        originalEnquiryRef.current = INITIAL_ENQUIRY;
        setEnquiryExists(false);
        setEnquiryFetchError(null);

    }, [open, contactData?.crmId]);

    useEffect(() => {
        if (!open || !contactData?.crmId || !onFetchEnquiryRef.current) return;

        let cancelled = false;
        setEnquiryLoading(true);
        setEnquiryFetchError(null);

        onFetchEnquiryRef
            .current(contactData.crmId)
            .then(fetched => {
                if (cancelled) return;
                if (fetched) {
                    const loaded = { ...INITIAL_ENQUIRY, ...fetched };
                    setEnquiryForm(loaded);
                    originalEnquiryRef.current = loaded;
                    setEnquiryExists(true);
                } else {
                    // No enquiry yet for this contact - not an error, tab just
                    // stays locked until the stage change on General creates one.
                    setEnquiryExists(false);
                }
            })
            .catch(err => {
                if (cancelled) return;
                setEnquiryFetchError(err);
                onEnquiryFetchErrorRef.current(err);
            })
            .finally(() => {
                if (!cancelled) setEnquiryLoading(false);
            });

        return () => {
            cancelled = true;
        };

    }, [open, contactData?.crmId]);


    const cancelEditing = useCallback(() => {
        if (!isEdit) return;
        setGeneralForm(originalGeneralRef.current);
        setEnquiryForm(originalEnquiryRef.current);
        setGeneralErrors({});
        setEnquiryErrors({});

        onClose();
    }, [isEdit]);

    // ---- General tab ----

    const handleGeneralChange = field => event => {
        const value = event.target.value;
        setGeneralForm(prev => ({ ...prev, [field]: value }));
        if (generalErrors[field]) setGeneralErrors(prev => ({ ...prev, [field]: null }));
    };


    const handleDropPinChange = useCallback((value) => {
        const coords = parseLatLngFromGoogleMapsUrl(value);
        setGeneralForm(prev => ({
            ...prev,
            dropPin: value,
            ...(coords ? { dropPinLatitude: coords.lat, dropPinLongitude: coords.lng } : {}),
        }));
    }, []);

    const handleStageChange = event => {
        const selectedName = event.target.value;
        const stageObj = stages.find(s => s.label === selectedName);
        setGeneralForm(prev => ({
            ...prev,
            crmStageName: selectedName,
            crmStageId: stageObj?.StageId || stageObj?.value || '',
        }));
    };

    const ADD_REQUIRED_FIELDS_LABELS = {
        crmTeamId: 'Team is required',
        crmStageId: 'Stage is required',
        crmSalesRep: 'Sales rep is required',
        crmProjectType: 'Contact type is required', // see note below
        crmContactFirst: 'First name is required',
        crmContactPhone: 'Phone is required',
    };

    const validateGeneral = useCallback(() => {
        const tempErrors = {};

        if (isEdit) {
            if (!generalForm.crmContactFirst?.trim()) tempErrors.crmContactFirst = 'First name is required';
            if (!generalForm.crmContactLast?.trim()) tempErrors.crmContactLast = 'Last name is required';
            if (!generalForm.crmCompany?.trim()) tempErrors.crmCompany = 'Company is required';
            if (!generalForm.crmAddressCountry?.trim()) tempErrors.crmAddressCountry = 'Country is required';
        } else {
            // Add Contact: only Team, Stage, Sales Rep, Contact Type, First Name, Phone are mandatory.
            Object.entries(ADD_REQUIRED_FIELDS_LABELS).forEach(([key, message]) => {
                if (!generalForm[key]?.toString().trim()) tempErrors[key] = message;
            });
        }

        // Format checks apply either way, only when a value is present.
        if (generalForm.crmContactEmail && !/\S+@\S+\.\S+/.test(generalForm.crmContactEmail)) {
            tempErrors.crmContactEmail = 'Invalid email address';
        }
        if (generalForm.crmContactPhone && !validatePhone(generalForm.crmPhoneCountryCode, generalForm.crmContactPhone)) {
            tempErrors.crmContactPhone = `Invalid ${generalForm.crmPhoneCountryCode === '+353' ? 'Ireland (+353)' : 'UK (+44)'} phone number format`;
        }

        setGeneralErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    }, [generalForm, isEdit]);

    const submitGeneral = useCallback(async () => {
        if (!validateGeneral()) return false;

        const payload = buildContactPayload(generalForm, isEdit);

        setSavingGeneral(true);
        try {
            const result = await onSaveContact(payload, isEdit);

            const savedGeneral = result?.contact?.crmId ? { ...payload, crmId: result.contact.crmId } : payload;
            setGeneralForm(savedGeneral);
            originalGeneralRef.current = savedGeneral;

            if (result?.enquiry) {
                // Auto-switch to enquiry tab if server generated one
                const newEnquiry = { ...INITIAL_ENQUIRY, ...result.enquiry };
                setEnquiryForm(newEnquiry);
                originalEnquiryRef.current = newEnquiry;
                setEnquiryExists(true);
                setActiveTab('enquiry');
                onEnquiryAutoCreated(result.enquiry);
            } else {
                // Close the modal if no enquiry step is needed
                onClose();
            }
            return true;
        } finally {
            setSavingGeneral(false);
        }
    }, [generalForm, isEdit, onSaveContact, onEnquiryAutoCreated, validateGeneral, onClose]);

    // ---- Enquiry tab ----

    const handleEnquiryChange = field => event => {
        const value = event.target.value;
        setEnquiryForm(prev => ({ ...prev, [field]: value }));
        if (enquiryErrors[field]) setEnquiryErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateEnquiry = useCallback(() => {

        setEnquiryErrors({});
        return true;
    }, []);

    const submitEnquiry = useCallback(async () => {
        if (!validateEnquiry()) return false;
        setSavingEnquiry(true);
        try {
            const payload = { ...enquiryForm, crmId: generalForm.crmId };
            await onSaveEnquiry(payload, enquiryExists);
            setEnquiryForm(payload);
            originalEnquiryRef.current = payload;
            setEnquiryExists(true);
            return true;
        } finally {
            setSavingEnquiry(false);
        }
    }, [enquiryForm, generalForm.crmId, enquiryExists, onSaveEnquiry, validateEnquiry]);

    return {
        isEdit,
        cancelEditing,
        activeTab,
        setActiveTab,
        isEnquiryUnlocked: enquiryExists,
        isEnquiryLoading: enquiryLoading,
        enquiryFetchError,
        isNotesUnlocked: Boolean(generalForm.crmId),
        general: {
            formData: generalForm,
            errors: generalErrors,
            handleChange: handleGeneralChange,
            handleStageChange,
            handleDropPinChange,
            submit: submitGeneral,
            saving: savingGeneral,
        },
        enquiry: {
            formData: enquiryForm,
            errors: enquiryErrors,
            handleChange: handleEnquiryChange,
            submit: submitEnquiry,
            saving: savingEnquiry,
        },
    };
}